import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, useScroll, AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import { Menu, X, Phone } from "lucide-react";
import { GM_NAV, HG_NAV, LOCATIONS, SOCIAL, hgLink, trackOutbound, trackPhoneClick, HG_TRIAL_PATH } from "../data/site";
import { NewsletterForm } from "./shared";

export const LenisProvider = ({ children }) => {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.11 });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  return children;
};

export const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  return <motion.div className="fixed top-0 left-0 right-0 h-[3px] bg-white text-[#0A0B0D] origin-left z-[60]" style={{ scaleX: scrollYProgress }} aria-hidden="true" />;
};

export const SiteSwitcher = () => {
  const { pathname } = useLocation();
  const onHG = pathname.startsWith("/hiddengym");
  return (
    <div className="bg-[#131518] border-b border-white/10 text-xs" role="navigation" aria-label="Site switcher">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-1 h-9">
        <span className="text-white/40 font-mono tracking-widest uppercase mr-2 hidden sm:inline">Preview:</span>
        <Link to="/" data-testid="site-switcher-guymezger-btn" className={`px-3 py-1 font-mono uppercase tracking-wider transition-colors ${!onHG ? "bg-white text-[#0A0B0D]" : "text-white/60 hover:text-white"}`}>guymezger.com</Link>
        <Link to="/hiddengym/martial-arts" data-testid="site-switcher-hiddengym-btn" className={`px-3 py-1 font-mono uppercase tracking-wider transition-colors ${onHG ? "bg-white text-[#0A0B0D]" : "text-white/60 hover:text-white"}`}>hiddengym · martial arts</Link>
      </div>
    </div>
  );
};

const NavShell = ({ brand, nav, accent, cta, mobileTestId }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A0B0D]/85 border-b border-white/10 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        {brand}
        <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `text-sm font-medium tracking-wide transition-colors ${isActive ? accent : "text-white/70 hover:text-white"}`}>
              {n.label}
            </NavLink>
          ))}
          {cta}
        </nav>
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="Toggle menu" data-testid={mobileTestId}>
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="lg:hidden overflow-hidden border-t border-white/10 bg-[#0A0B0D]" aria-label="Mobile">
            <div className="px-4 py-4 flex flex-col gap-1">
              {nav.map((n) => (
                <NavLink key={n.to} to={n.to} className="py-3 px-2 font-display font-bold uppercase tracking-wide text-lg text-white/80 hover:text-white border-b border-white/5">{n.label}</NavLink>
              ))}
              <div className="pt-3">{cta}</div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export const GMNav = () => (
  <NavShell
    brand={
      <Link to="/" className="flex flex-col leading-none" data-testid="gm-logo-link">
        <span className="font-display font-black uppercase tracking-tight text-xl">Mezger <span className="text-neutral-400">Martial Arts</span></span>
        <span className="font-mono text-[10px] tracking-[0.2em] text-white/50 uppercase">at Hidden Gym · Richardson & North Dallas</span>
      </Link>
    }
    nav={GM_NAV}
    accent="text-neutral-400"
    mobileTestId="gm-mobile-menu-btn"
    cta={
      <Link to={hgLink(HG_TRIAL_PATH, "nav")} onClick={() => trackOutbound("nav", "nav")} data-testid="nav-book-trial-btn"
        className="bg-[#111315] border border-white/20 hover:bg-neutral-200 hover:text-[#0A0B0D] text-white font-display font-bold uppercase tracking-wide text-sm px-5 py-2.5 transition-colors">
        Free Trial
      </Link>
    }
  />
);

export const HGNav = () => (
  <NavShell
    brand={
      <Link to="/hiddengym/martial-arts" className="flex flex-col leading-none" data-testid="hg-logo-link">
        <span className="font-display font-black uppercase tracking-tight text-xl">Hidden <span className="text-neutral-400">Gym</span></span>
        <span className="font-mono text-[10px] tracking-[0.2em] text-white/50 uppercase">Martial Arts · Led by Guy Mezger</span>
      </Link>
    }
    nav={HG_NAV}
    accent="text-neutral-400"
    mobileTestId="hg-mobile-menu-btn"
    cta={
      <Link to="/hiddengym/martial-arts/free-trial" data-testid="hg-nav-trial-btn"
        className="bg-[#111315] border border-white/20 hover:bg-neutral-200 hover:text-[#0A0B0D] text-white font-display font-bold uppercase tracking-wide text-sm px-5 py-2.5 transition-colors">
        Free Trial
      </Link>
    }
  />
);

export const GMFooter = () => (
  <footer className="bg-[#0A0B0D] text-white border-t border-white/10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid gap-10 md:grid-cols-4">
      <div className="md:col-span-2">
        <p className="font-display font-black uppercase text-2xl">Mezger <span className="text-neutral-400">Martial Arts</span></p>
        <p className="mt-3 text-white/60 text-sm max-w-sm">The martial arts program at <Link to="/hiddengym/martial-arts" className="underline hover:text-white" data-testid="footer-hiddengym-link">Hidden Gym</Link>, led by Guy Mezger. Two North Texas locations. Your first class is free.</p>
        <div className="mt-6">
          <p className="font-mono text-xs tracking-widest uppercase text-white/40 mb-2">Newsletter</p>
          <NewsletterForm dark sourcePage="gm-footer" />
        </div>
      </div>
      <div>
        <p className="font-mono text-xs tracking-widest uppercase text-white/40 mb-3">Explore</p>
        <ul className="space-y-2 text-sm text-white/70">
          {[...GM_NAV, { label: "Contact", to: "/contact" }, { label: "Waiver", to: "/waiver" }, { label: "Blog", to: "/blog" }].map((n) => (
            <li key={n.to}><Link to={n.to} className="hover:text-white">{n.label}</Link></li>
          ))}
        </ul>
      </div>
      <div>
        <p className="font-mono text-xs tracking-widest uppercase text-white/40 mb-3">Locations</p>
        {LOCATIONS.map((l) => (
          <div key={l.slug} className="mb-4 text-sm text-white/70">
            <Link to={`/locations/${l.slug}`} className="font-bold text-white hover:text-neutral-500">{l.short}</Link>
            <p className="mt-1">{l.address}</p>
            <a href={l.phoneHref} onClick={() => trackPhoneClick(l.slug, l.phoneHref)} className="flex items-center gap-1 mt-1 hover:text-white" data-testid={`footer-phone-${l.slug}`}><Phone size={12} /> {l.phone}</a>
          </div>
        ))}
        <div className="flex gap-4 text-sm text-white/50">
          <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-white">Facebook</a>
          <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-white">Instagram</a>
        </div>
      </div>
    </div>
    <div className="border-t border-white/10 py-6 text-center text-xs text-white/40 font-mono tracking-wider">
      © {new Date().getFullYear()} MEZGER MARTIAL ARTS · A PROGRAM OF HIDDEN GYM · <Link to="/hiddengym/membership" className="underline">MEMBERSHIPS BY HIDDEN GYM</Link>
    </div>
  </footer>
);

export const HGFooter = () => (
  <footer className="bg-[#131518] text-white border-t border-white/10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid gap-10 md:grid-cols-3">
      <div>
        <p className="font-display font-black uppercase text-2xl">Hidden <span className="text-neutral-400">Gym</span></p>
        <p className="mt-3 text-white/60 text-sm">Home of Mezger Martial Arts, led by Guy Mezger. Learn his full story at <Link to="/about" className="underline hover:text-white" data-testid="hg-footer-guymezger-link">guymezger.com</Link>.</p>
      </div>
      <div>
        <p className="font-mono text-xs tracking-widest uppercase text-white/40 mb-3">Martial Arts</p>
        <ul className="space-y-2 text-sm text-white/70">
          {HG_NAV.map((n) => <li key={n.to}><Link to={n.to} className="hover:text-white">{n.label}</Link></li>)}
          <li><Link to="/hiddengym/martial-arts/free-trial" className="hover:text-white">Free Trial</Link></li>
        </ul>
      </div>
      <div>
        <p className="font-mono text-xs tracking-widest uppercase text-white/40 mb-3">Locations</p>
        {LOCATIONS.map((l) => (
          <p key={l.slug} className="text-sm text-white/70 mb-2">{l.short}: {l.address} · {l.phone}</p>
        ))}
      </div>
    </div>
    <div className="border-t border-white/10 py-6 text-center text-xs text-white/40 font-mono tracking-wider">
      © {new Date().getFullYear()} HIDDEN GYM · MEZGER MARTIAL ARTS AT HIDDEN GYM
    </div>
  </footer>
);

export const StickyTrialCTA = () => (
  <div className="fixed bottom-0 inset-x-0 z-50 lg:hidden bg-[#0A0B0D]/95 backdrop-blur border-t border-white/10 p-3 flex gap-2" role="region" aria-label="Quick actions">
    <Link to={hgLink(HG_TRIAL_PATH, "sticky-mobile")} onClick={() => trackOutbound("sticky", "sticky-mobile")} data-testid="sticky-mobile-trial-btn"
      className="flex-1 bg-white text-[#0A0B0D] font-display font-bold uppercase tracking-wide text-center py-3">
      Book Free Trial
    </Link>
    <Link to="/schedule" data-testid="sticky-mobile-schedule-btn" className="flex-1 border border-white/30 text-white font-display font-bold uppercase tracking-wide text-center py-3">
      Schedule
    </Link>
  </div>
);

export const TrialPopup = () => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("trial-popup") === "1");
  useEffect(() => {
    if (dismissed) return;
    const timer = setTimeout(() => setShow(true), 25000);
    const onScroll = () => {
      const p = window.scrollY / (document.body.scrollHeight - window.innerHeight);
      if (p > 0.5) setShow(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { clearTimeout(timer); window.removeEventListener("scroll", onScroll); };
  }, [dismissed]);
  const close = () => { setShow(false); setDismissed(true); sessionStorage.setItem("trial-popup", "1"); };
  return (
    <AnimatePresence>
      {show && !dismissed && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Free trial offer" onClick={close}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            className="bg-[#0A0B0D] border border-white/15 text-white max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()} data-testid="trial-popup">
            <button onClick={close} className="absolute top-3 right-3 p-2 text-white/60 hover:text-white" aria-label="Close" data-testid="trial-popup-close-btn"><X size={20} /></button>
            <p className="font-mono text-xs tracking-[0.22em] uppercase text-neutral-400">First class on us</p>
            <h2 className="font-display font-black uppercase text-3xl mt-2 leading-none">Your first class at Hidden Gym is free.</h2>
            <p className="mt-3 text-white/70 text-sm">No experience needed.</p>
            <Link to={hgLink(HG_TRIAL_PATH, "popup")} onClick={() => { trackOutbound("popup", "popup"); close(); }} data-testid="trial-popup-cta-btn"
              className="mt-6 block bg-[#111315] border border-white/20 hover:bg-neutral-200 hover:text-[#0A0B0D] text-white font-display font-bold uppercase tracking-wide text-center py-4 transition-colors">
              Claim My Free Trial
            </Link>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
